import React, { useMemo, useEffect, useState, useCallback } from 'react'
import { Text, View } from 'react-native'
import { observer } from 'mobx-react-lite'
import { ListItem } from '../atoms/builder-2.0/ListItem'
import { ArrowBlue, BlueTapeIconDark, InfoIcon } from '../../assets/icons'
import { isWeb } from '../../utils/helpers/commonUtils'
import { useStore } from '../../store'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import cookies from '../../utils/service/cooker'
import { Trans, useTranslation } from 'react-i18next'
import { dispatcher, pathFactory } from '../../screens/links'
import { LoanAppDetails } from '../../screens/Contractor/InvoicesTab/Invoice/InvoiceDetails'
import {
  APPROVED_STATUSES,
  PROCESSING_STATUSES,
} from '@linqpal/models/src/dictionaries'

interface PropTypes {
  navigation: any
  setModal: (a) => void
  onPress: () => void
  showInfo?: boolean
  amountLessThanMin?: boolean
  loanAppDetails: LoanAppDetails
  disabled?: boolean
}

export const ApplyBTCreditItem: React.FC<PropTypes> = observer(
  ({
    navigation,
    setModal,
    onPress,
    showInfo,
    amountLessThanMin = false,
    loanAppDetails,
    disabled,
  }) => {
    const { userStore } = useStore()
    const { t } = useTranslation()
    const { currentInvoiceCreditStatus = '' } = loanAppDetails
    const [textData, setTextData] = useState({
      info: t('payWithCreditButton.does-not-affect-credit-score'),
      subtitle: t('payWithCreditButton.no-fees'),
    })

    const isUserAuthenticated = isWeb
      ? !!cookies.get('session')
      : userStore.isAuthenticated

    const title = useMemo(() => {
      if (PROCESSING_STATUSES.find((s) => s === currentInvoiceCreditStatus))
        return (
          <Text style={{ fontWeight: '600', fontSize: 16 }}>
            {t('payWithCreditButton.app-processing')}
          </Text>
        )
      else
        return (
          <Trans i18nKey="pay-with-btc" t={t}>
            <Text style={{ fontWeight: '600', fontSize: 16 }}>Pay with</Text>{' '}
            <Text style={{ color: commonColors.accentText }}>
              BlueTape Credit
            </Text>{' '}
          </Trans>
        )
    }, [currentInvoiceCreditStatus, t])

    const updateTextData = useCallback(() => {
      if (amountLessThanMin) {
        setTextData({
          info: t('payWithCreditButton.min-amount-applicable'),
          subtitle: t('payWithCreditButton.not-eligible'),
        })
      }
    }, [amountLessThanMin, t])

    useEffect(() => {
      if (isUserAuthenticated) {
        userStore.checkInvoicesToPay()
      }
      updateTextData()
    }, [isUserAuthenticated, updateTextData, userStore])

    const isDisabled = () => {
      return (
        disabled ||
        !!PROCESSING_STATUSES.find((s) => s === currentInvoiceCreditStatus) ||
        !!APPROVED_STATUSES.find((s) => s === currentInvoiceCreditStatus) ||
        amountLessThanMin
      )
    }

    const handleOnclick = () => {
      if (setModal) {
        setModal(false)
      }
      const action = dispatcher(pathFactory('Console.Credit.CreditApplication'))
      navigation.navigate(action.name, action.params)
    }
    return (
      <View>
        <ListItem
          disabled={isDisabled()}
          style={{
            alignSelf: 'center',
            backgroundColor: 'white',
            marginBottom: 0,
            height: 76,
            borderWidth: 1,
            borderColor: '#CCD6DD',
            borderRadius: 8,
            shadowColor: 'black',
            shadowOpacity: 0.06,
            shadowRadius: 10,
          }}
          otherStyles={{
            title: { fontWeight: '600', lineHeight: 22 },
            subTitle: { color: commonColors.textDark },
            frontIcon: { width: 34, height: 34, marginTop: 19 },
          }}
          onPress={onPress || handleOnclick}
          icon={<BlueTapeIconDark />}
          endIcon={<ArrowBlue style={{ height: 25.2, width: 25.2 }} />}
          title={title}
          subtitle={textData.subtitle}
        />
        {showInfo && (
          <View style={{ flexDirection: 'row', marginTop: 5 }}>
            <InfoIcon style={{ alignSelf: 'center' }} />
            <Text
              style={{
                fontSize: 12,
                color: '#99ADBA',
                marginLeft: 8,
                fontWeight: '500',
              }}
            >
              {textData.info}
            </Text>
          </View>
        )}
      </View>
    )
  },
)
